import React from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";

// shadcn/ui
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Separator } from "components/ui/separator";

// icons
import {
  Loader2,
  Building2,
  ClipboardList,
  HelpCircle,
  FileText,
  CalendarRange,
  Brain,
  Upload,
  Settings,
  BookOpen,
  Activity,
  BarChart3,
  MessageSquare,
  Users,
  FolderKanban,
} from "lucide-react";

import Logo from "assets/images/cog.png";

const Dashboard = () => {
  const navigate = useNavigate();
  const { get_auth } = useSelector(
    (state) => ({
      get_auth: state.auth,
    }),
    shallowEqual
  );

  const quickLinks = [
    { title: "Organizations", to: "/app/organizations", icon: Building2, description: "Manage organizations" },
    { title: "Surveys", to: "/app/brief-schedule", icon: ClipboardList, description: "Run and track surveys" },
    { title: "Questions", to: "/app/questions", icon: HelpCircle, description: "Question library" },
    { title: "Categories", to: "/app/categories", icon: FolderKanban, description: "Taxonomy & compliance" },
    { title: "Email Templates", to: "/app/email-templates", icon: FileText, description: "Transactional emails" },
    { title: "Schedule Reports", to: "/app/schedule-template-report", icon: CalendarRange, description: "Recurring reports" },
    { title: "LLM Usage", to: "/app/llm-usage", icon: Brain, description: "AI usage logs" },
    { title: "Public Uploads", to: "/app/public-uploads", icon: Upload, description: "Shared assets" },
  ];

  const moreLinks = [
    { title: "Action Planning", to: "/app/action", icon: CalendarRange },
    { title: "Marketing ROI", to: "/app/marketing-roi", icon: BarChart3 },
    { title: "Groups", to: "/app/groups", icon: Users },
    { title: "PDF Reports", to: "/app/pdf-reports", icon: FileText },
    { title: "Presentation Reports", to: "/app/presentation-reports", icon: FileText },
    { title: "Build Apps", to: "/app/server-scripts", icon: Settings },
    { title: "Settings", to: "/app/settings", icon: Settings },
    { title: "Documentation", to: "/app/api-documentation", icon: BookOpen },
    { title: "Activity Log", to: "/app/activity-log", icon: Activity },
  ];

  return (
    <div className="w-full">
      <div className="px-6 pt-6 pb-4">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <img src={Logo} alt="Innerlogic" className="h-12 w-12" />
                  <div>
                    <CardTitle className="text-2xl">
                      {get_auth?.employee?.first_name ? `Welcome, ${get_auth?.employee?.first_name}!` : "Welcome"}
                    </CardTitle>
                    <CardDescription>Innerlogic Admin – quick access to your core tools</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <MessageSquare className="h-4 w-4" />
                  <span>This app is for Innerlogic employees and partners only.</span>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">At a glance</CardTitle>
                <CardDescription>Jump into the most used areas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="justify-start gap-2" onClick={() => navigate("/app/brief-schedule")}> 
                    <ClipboardList className="h-4 w-4" /> Surveys
                  </Button>
                  <Button variant="outline" className="justify-start gap-2" onClick={() => navigate("/app/organizations")}>
                    <Building2 className="h-4 w-4" /> Orgs
                  </Button>
                  <Button variant="outline" className="justify-start gap-2" onClick={() => navigate("/app/questions")}>
                    <HelpCircle className="h-4 w-4" /> Questions
                  </Button>
                  <Button variant="outline" className="justify-start gap-2" onClick={() => navigate("/app/categories")}>
                    <FolderKanban className="h-4 w-4" /> Categories
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold tracking-tight">Quick navigation</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {quickLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Card
                    key={link.title}
                    className="group cursor-pointer transition hover:shadow-md"
                    onClick={() => navigate(link.to)}
                  >
                    <CardHeader className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                            <Icon className="h-4 w-4" />
                          </span>
                          <CardTitle className="text-base">{link.title}</CardTitle>
                        </div>
                      </div>
                      <CardDescription>{link.description}</CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>

          <Separator />

          <div>
            <div className="mb-3 flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold tracking-tight">More</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {moreLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Card
                    key={link.title}
                    className="group cursor-pointer transition hover:shadow-md"
                    onClick={() => navigate(link.to)}
                  >
                    <CardHeader className="space-y-1">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                          <Icon className="h-4 w-4" />
                        </span>
                        <CardTitle className="text-base">{link.title}</CardTitle>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
